/**
 * Comprehensive logging service for the application
 * Replaces console.log statements with structured, level-based logging
 */

const LogLevel = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
} as const;

type LogLevel = typeof LogLevel[keyof typeof LogLevel];

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: Date;
  args: any[];
}

export class Logger {
  private static currentLevel: LogLevel = Logger.getDefaultLevel();
  private static readonly levelNames = {
    [LogLevel.DEBUG]: 'DEBUG',
    [LogLevel.INFO]: 'INFO',
    [LogLevel.WARN]: 'WARN',
    [LogLevel.ERROR]: 'ERROR',
  };

  private static readonly levelEmojis = {
    [LogLevel.DEBUG]: '🐛',
    [LogLevel.INFO]: 'ℹ️',
    [LogLevel.WARN]: '⚠️',
    [LogLevel.ERROR]: '❌',
  };

  private static readonly levelColors = {
    [LogLevel.DEBUG]: '#6B7280', // gray-500
    [LogLevel.INFO]: '#3B82F6',  // blue-500
    [LogLevel.WARN]: '#F59E0B',  // amber-500
    [LogLevel.ERROR]: '#EF4444', // red-500
  };

  /**
   * Get the default log level based on environment
   */
  private static getDefaultLevel(): LogLevel {
    // Use Vite's environment detection
    return import.meta.env.DEV ? LogLevel.DEBUG : LogLevel.WARN;
  }

  /**
   * Set the current log level
   * @param level - The minimum level to log
   */
  static setLevel(level: LogLevel): void {
    Logger.currentLevel = level;
  }

  /**
   * Get the current log level
   */
  static getLevel(): LogLevel {
    return Logger.currentLevel;
  }

  /**
   * Format timestamp for log output
   */
  private static formatTimestamp(date: Date): string {
    return date.toISOString().replace('T', ' ').replace('Z', '');
  }

  /**
   * Create a formatted log prefix
   */
  private static createPrefix(level: LogLevel): string {
    const timestamp = Logger.formatTimestamp(new Date());
    const emoji = Logger.levelEmojis[level];
    const levelName = Logger.levelNames[level];
    
    return `[${timestamp}] ${emoji} ${levelName}:`;
  }

  /**
   * Internal logging method
   */
  private static log(level: LogLevel, message: string, ...args: any[]): void {
    if (level < Logger.currentLevel) {
      return;
    }

    const prefix = Logger.createPrefix(level);
    const color = Logger.levelColors[level];

    // In development, use styled console output
    if (import.meta.env.DEV) {
      console.log(
        `%c${prefix}`,
        `color: ${color}; font-weight: bold;`,
        message,
        ...args
      );
    } else {
      // In production, use plain console output
      switch (level) {
        case LogLevel.DEBUG:
          console.debug(prefix, message, ...args);
          break;
        case LogLevel.INFO:
          console.info(prefix, message, ...args);
          break;
        case LogLevel.WARN:
          console.warn(prefix, message, ...args);
          break;
        case LogLevel.ERROR:
          console.error(prefix, message, ...args);
          break;
      }
    }
  }

  /**
   * Log a debug message
   * Only shown in development environment by default
   */
  static debug(message: string, ...args: any[]): void {
    Logger.log(LogLevel.DEBUG, message, ...args);
  }

  /**
   * Log an info message
   */
  static info(message: string, ...args: any[]): void {
    Logger.log(LogLevel.INFO, message, ...args);
  }

  /**
   * Log a warning message
   */
  static warn(message: string, ...args: any[]): void {
    Logger.log(LogLevel.WARN, message, ...args);
  }

  /**
   * Log an error message
   */
  static error(message: string, ...args: any[]): void {
    Logger.log(LogLevel.ERROR, message, ...args);
  }

  /**
   * Create a scoped logger with a prefix
   * Useful for component or service-specific logging
   */
  static createScoped(scope: string) {
    return {
      debug: (message: string, ...args: any[]) => 
        Logger.debug(`[${scope}] ${message}`, ...args),
      info: (message: string, ...args: any[]) => 
        Logger.info(`[${scope}] ${message}`, ...args),
      warn: (message: string, ...args: any[]) => 
        Logger.warn(`[${scope}] ${message}`, ...args),
      error: (message: string, ...args: any[]) =>
        Logger.error(`[${scope}] ${message}`, ...args),
    };
  }

  /**
   * Log performance timing information
   */
  static time(label: string): void {
    if (Logger.currentLevel <= LogLevel.DEBUG) {
      console.time(`⏱️ ${label}`);
    }
  }

  /**
   * End performance timing and log the result
   */
  static timeEnd(label: string): void {
    if (Logger.currentLevel <= LogLevel.DEBUG) {
      console.timeEnd(`⏱️ ${label}`);
    }
  }

  /**
   * Log a table of data (useful for debugging)
   */
  static table(data: any, columns?: string[]): void {
    if (Logger.currentLevel <= LogLevel.DEBUG) {
      const prefix = Logger.createPrefix(LogLevel.DEBUG);
      console.log(`%c${prefix}`, `color: ${Logger.levelColors[LogLevel.DEBUG]}; font-weight: bold;`, 'Table data:');
      console.table(data, columns);
    }
  }

  /**
   * Log a group of related messages
   */
  static group(label: string): void {
    if (Logger.currentLevel <= LogLevel.DEBUG) {
      const prefix = Logger.createPrefix(LogLevel.DEBUG);
      console.group(`%c${prefix}`, `color: ${Logger.levelColors[LogLevel.DEBUG]}; font-weight: bold;`, label);
    }
  }

  /**
   * End a log group
   */
  static groupEnd(): void {
    if (Logger.currentLevel <= LogLevel.DEBUG) {
      console.groupEnd();
    }
  }

  /**
   * Log an object with proper formatting
   */
  static object(label: string, obj: any): void {
    if (Logger.currentLevel <= LogLevel.DEBUG) {
      Logger.debug(`${label}:`, obj);
    }
  }

  /**
   * Conditional logging - only logs if condition is true
   */
  static assert(condition: boolean, message: string, ...args: any[]): void {
    if (!condition) {
      Logger.error(`Assertion failed: ${message}`, ...args);
    }
  }

  /**
   * Log environment information (useful for debugging)
   */
  static env(): void {
    if (Logger.currentLevel <= LogLevel.DEBUG) {
      Logger.group('Environment Information');
      Logger.debug('Development mode:', import.meta.env.DEV);
      Logger.debug('Production mode:', import.meta.env.PROD);
      Logger.debug('Current log level:', Logger.levelNames[Logger.currentLevel]);
      Logger.debug('User agent:', navigator.userAgent);
      Logger.debug('URL:', window.location.href);
      Logger.groupEnd();
    }
  }
}

// Convenience export for shorter imports
export const log = Logger;

